/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { FC, createContext, useState, PropsWithChildren, ReactNode } from 'react';
import { SnackbarOrigin } from '@mui/material';
import { noop } from 'lodash';

export type ConfirmationDialogText = {
	title?: ReactNode;
	message: ReactNode;
	cancel?: ReactNode;
	ok?: ReactNode;
};

export type NotificationMessageConfirmationType = {
	body: ConfirmationDialogText;
	onConfirm: () => Promise<unknown>;
	onCancel: () => Promise<unknown>;
};

export type NotificationMessageOptionsType = {
	severity?:
		| 'error'
		| 'info'
		| 'success'
		| 'warning'
		| 'snackbar'
		| 'confirmation'
		| 'successAuth'
		| 'snackbar-error';
	anchorOrigin?: SnackbarOrigin;
	snackbarDuration?: number;
	snackbarSeverity?: 'error' | 'info' | 'success' | 'warning';
	confirmationDialog?: NotificationMessageConfirmationType;
};

export type NotificationText = string | JSX.Element | JSX.Element[];

type NotificationMessageType = NotificationMessageOptionsType & {
	title?: NotificationText;
	text: NotificationText;
	isAddToCart?: boolean;
};

export type NotificationsContextType = {
	message: NotificationMessageType;
	setMessage: (message: NotificationMessageType) => void;
};

export const NotificationsContext = createContext<NotificationsContextType>({
	message: { text: '' },
	setMessage: noop,
});

export const NotificationsProvider: FC<PropsWithChildren> = ({ children }) => {
	const [message, setMessage] = useState<NotificationMessageType>({
		text: '',
	});

	return (
		<NotificationsContext.Provider value={{ message, setMessage }}>
			{children}
		</NotificationsContext.Provider>
	);
};
